import type { CallMedia } from "@rhema/client";
import {
  Avatar,
  Button,
  ChevronDownIcon,
  cn,
  EmptyState,
  FlipCameraIcon,
  focusRing,
  HandIcon,
  IconButton,
  LockIcon,
  MicOffIcon,
  PhoneIcon,
  ScreenShareIcon,
  Select,
  Sheet,
  SlidersIcon,
  UsersIcon,
  VideoIcon,
} from "@rhema/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useIsDesktop } from "../app/media";
import { formatTimer, useCalls, useElapsed } from "../calls/CallProvider";
import { canFlipCamera, canShareScreen, type DeviceKind, type Tile } from "../calls/media";
import { callTitle, isGroupCall } from "../calls/people";
import { gridColumns, ParticipantTile, TrackVideo } from "../calls/Tiles";
import { toneFor } from "../data/view";
import { useClientState } from "../session/session";

function Control({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <IconButton
      label={label}
      shape="circle"
      size={50}
      variant={pressed ? "primary" : "soft"}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
}

const DEVICE_LABELS: Record<DeviceKind, string> = {
  audioinput: "Microphone",
  videoinput: "Camera",
  audiooutput: "Speaker",
};

function DevicePicker({ kind }: { kind: DeviceKind }) {
  const { room } = useCalls();
  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null);
  const [current, setCurrent] = useState<string>("");
  useEffect(() => {
    let live = true;
    void room?.devices(kind).then((list) => {
      if (!live) return;
      setDevices(list);
      setCurrent(room.activeDevice(kind) ?? list[0]?.deviceId ?? "");
    });
    return () => {
      live = false;
    };
  }, [room, kind]);
  if (devices && !devices.length) {
    if (kind === "audiooutput")
      return (
        <p className="m-0 text-[13px] text-ink-secondary">
          This browser plays calls through its default speaker.
        </p>
      );
    return (
      <p className="m-0 text-[13px] text-ink-secondary">No {DEVICE_LABELS[kind].toLowerCase()} was found.</p>
    );
  }
  return (
    <Select
      label={DEVICE_LABELS[kind]}
      value={current}
      disabled={!devices}
      options={(devices ?? []).map((d, i) => ({
        value: d.deviceId,
        label: d.label || `${DEVICE_LABELS[kind]} ${i + 1}`,
      }))}
      onChange={(deviceId) => {
        setCurrent(deviceId);
        void room?.switchDevice(kind, deviceId);
      }}
      menuClassName="max-w-[min(420px,calc(100vw-24px))]"
    />
  );
}

function DeviceSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Devices">
      <div className="mt-4 flex flex-col gap-4">
        <DevicePicker kind="audioinput" />
        <DevicePicker kind="videoinput" />
        <DevicePicker kind="audiooutput" />
      </div>
    </Sheet>
  );
}

function Notice({ children, onDismiss }: { children: ReactNode; onDismiss?: () => void }) {
  return (
    <div className="mx-4 flex items-start gap-3 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-[13px] text-ink">
      <p role="alert" className="m-0 flex-1">
        {children}
      </p>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn("cursor-pointer border-0 bg-transparent p-0 font-semibold text-blue", focusRing)}
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

/** Before a call: start one, join one that is running, or read why it could not start. */
function CallLobby({ chatId }: { chatId: string }) {
  const state = useClientState();
  const { state: calls, start, accept, notice, dismissNotice } = useCalls();
  const navigate = useNavigate();
  const chat = state.chats[chatId];
  if (!chat) return <EmptyState title="Call not found" className="flex-1" />;
  const title = callTitle(state, { chat_id: chatId });
  const group = isGroupCall(state, chatId);
  const running = calls.history.find((c) => c.chat_id === chatId && !c.ended_at);
  const elsewhere = calls.active && calls.active.chatId !== chatId ? calls.active : null;
  const joining = calls.joining !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-header">
      <header className="flex flex-none items-center gap-2.5 px-4 pt-4 pb-3">
        <IconButton label="Back to chat" variant="linen" href={`/chats/${chatId}`}>
          <ChevronDownIcon size={22} strokeWidth={2.2} />
        </IconButton>
      </header>
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <Avatar
          name={title}
          tone={toneFor(chatId)}
          size={112}
          serif
          shape={group ? "rounded" : "circle"}
          icon={group ? <UsersIcon size={40} /> : undefined}
        />
        <h1 className="m-0 mt-2 font-serif text-[26px] font-medium">{title}</h1>
        <p className="m-0 flex items-center gap-1.5 text-[13px] text-ink-secondary">
          <LockIcon size={12} strokeWidth={2.6} />
          Calls are end-to-end encrypted.
        </p>
        {joining && <p className="m-0 text-[15px] text-ink-secondary">Connecting</p>}
      </div>
      {notice && <Notice onDismiss={dismissNotice}>{notice}</Notice>}
      <div className="flex flex-none flex-wrap justify-center gap-3 px-4 pt-4 pb-[max(30px,env(safe-area-inset-bottom))]">
        {elsewhere ? (
          <Button onClick={() => navigate(`/calls/${elsewhere.chatId}`)}>Return to your call</Button>
        ) : running ? (
          <Button
            icon={running.media === "video" ? <VideoIcon size={18} /> : <PhoneIcon size={18} />}
            disabled={joining}
            onClick={() => void accept(running.id)}
          >
            Join call
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              icon={<PhoneIcon size={18} />}
              disabled={joining}
              onClick={() => void start(chatId, "voice")}
            >
              Voice call
            </Button>
            <Button
              icon={<VideoIcon size={18} />}
              disabled={joining}
              onClick={() => void start(chatId, "video")}
            >
              Video call
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function InCall({ chatId }: { chatId: string }) {
  const state = useClientState();
  const { state: calls, room, snapshot, end, notice, dismissNotice } = useCalls();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [flippable, setFlippable] = useState(false);
  const active = calls.active;
  const answered = active?.call.answered_at ? Date.parse(active.call.answered_at) : null;
  const elapsed = useElapsed(answered);

  useEffect(() => {
    void canFlipCamera().then(setFlippable);
  }, []);

  if (!active) return null;
  const title = callTitle(state, active.call);
  const tiles = snapshot?.tiles ?? [];
  const me = tiles.find((t) => t.local);
  const remotes = tiles.filter((t) => !t.local);
  const screen = tiles.find((t) => t.screen);
  // While it rings there is nobody else yet, so show who is being called.
  const waiting: Tile[] = remotes.length
    ? remotes
    : (state.chats[chatId]?.members ?? [])
        .filter((m) => m.user.id !== state.me?.id)
        .slice(0, 31)
        .map((m) => ({
          identity: m.user.id,
          name: m.user.display_name,
          local: false,
          speaking: false,
          micOn: true,
          cameraOn: false,
          handRaised: false,
        }));
  const cols = gridColumns(waiting.length, desktop);
  const avatarSize = waiting.length <= 4 ? 92 : waiting.length <= 9 ? 64 : 44;

  const status = snapshot?.encrypted ? (
    <>
      <LockIcon size={12} strokeWidth={2.6} />
      Encrypted
    </>
  ) : snapshot?.status === "connected" ? (
    "Setting up encryption"
  ) : snapshot?.status === "reconnecting" ? (
    "Reconnecting"
  ) : (
    "Connecting"
  );
  const timer = answered ? formatTimer(elapsed) : remotes.length ? null : "Ringing";

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-header">
      <header className="flex flex-none items-center gap-2.5 px-4 pt-4 pb-3">
        <IconButton label="Minimise call" variant="linen" onClick={() => navigate(`/chats/${chatId}`)}>
          <ChevronDownIcon size={22} strokeWidth={2.2} />
        </IconButton>
        <span className="flex min-w-0 flex-1 flex-col items-center">
          <h1 className="m-0 truncate font-serif text-[21px] font-medium">{title}</h1>
          <span className="flex items-center gap-[5px] text-[13px] text-ink-secondary">
            <span data-testid="call-security" className="flex items-center gap-[5px]">
              {status}
            </span>
            {timer && (
              <>
                <span aria-hidden="true">·</span>
                <span data-testid="call-timer">{timer}</span>
              </>
            )}
          </span>
        </span>
        <IconButton label="Choose devices" variant="linen" onClick={() => setDevicesOpen(true)}>
          <SlidersIcon size={20} strokeWidth={2.2} />
        </IconButton>
      </header>

      {(notice || snapshot?.problem) && (
        <div className="flex flex-col gap-2 pb-2">
          {notice && <Notice onDismiss={dismissNotice}>{notice}</Notice>}
          {snapshot?.problem && <Notice onDismiss={() => room?.clearProblem()}>{snapshot.problem}</Notice>}
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col gap-2.5 px-4 pt-1 min-[900px]:mx-auto min-[900px]:w-full min-[900px]:max-w-[1100px]">
        {screen?.screen && (
          <div className="relative min-h-[200px] flex-[2] overflow-hidden rounded-panel bg-tone-stone">
            <TrackVideo
              track={screen.screen}
              fit="contain"
              label={screen.local ? "Your screen" : `${screen.name}'s screen`}
            />
          </div>
        )}
        <div
          className="grid min-h-0 flex-1 auto-rows-fr gap-2.5 overflow-y-auto"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {waiting.map((t) => (
            <ParticipantTile key={t.identity} tile={t} avatarSize={avatarSize} />
          ))}
        </div>
        {me && (
          <div className="absolute right-7 bottom-3 h-[130px] w-[94px] overflow-hidden rounded-[18px] border-[3px] border-linen bg-tone-sky shadow-float">
            {me.camera ? (
              <TrackVideo track={me.camera} mirror label="Your camera" />
            ) : (
              <span className="flex h-full flex-col items-center justify-center gap-2">
                <Avatar
                  name={state.me?.display_name ?? "You"}
                  src={state.me?.avatar_url}
                  tone="self"
                  size={50}
                />
                <span className="text-xs font-semibold text-ink">You</span>
              </span>
            )}
            {me.handRaised && (
              <span className="absolute top-1.5 right-1.5 rounded-full bg-blue p-1 text-white">
                <HandIcon size={12} strokeWidth={2.4} label="Your hand is raised" />
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex-none px-4 pt-4 pb-[max(30px,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex max-w-[520px] flex-wrap items-center justify-center gap-2 rounded-sheet bg-linen p-2.5 shadow-dock min-[420px]:justify-between">
          {flippable && snapshot?.cameraOn && (
            <Control label="Flip camera" onClick={() => void room?.flipCamera()}>
              <FlipCameraIcon size={21} strokeWidth={2.2} />
            </Control>
          )}
          <Control
            label={snapshot?.cameraOn ? "Turn camera off" : "Turn camera on"}
            pressed={!snapshot?.cameraOn}
            onClick={() => void room?.setCamera(!snapshot?.cameraOn)}
          >
            <VideoIcon size={21} strokeWidth={2.2} />
          </Control>
          <Control
            label={snapshot?.micOn ? "Mute microphone" : "Unmute microphone"}
            pressed={!snapshot?.micOn}
            onClick={() => void room?.setMic(!snapshot?.micOn)}
          >
            <MicOffIcon size={21} strokeWidth={2.2} />
          </Control>
          {canShareScreen() && (
            <Control
              label={snapshot?.screenOn ? "Stop sharing screen" : "Share screen"}
              pressed={snapshot?.screenOn}
              onClick={() => void room?.setScreen(!snapshot?.screenOn)}
            >
              <ScreenShareIcon size={21} strokeWidth={2.2} />
            </Control>
          )}
          <Control
            label={snapshot?.handRaised ? "Lower hand" : "Raise hand"}
            pressed={snapshot?.handRaised}
            onClick={() => void room?.setHand(!snapshot?.handRaised)}
          >
            <HandIcon size={21} strokeWidth={2.2} />
          </Control>
          <button
            type="button"
            aria-label="End call"
            onClick={() => void end()}
            className={cn(
              "flex h-[50px] w-[74px] cursor-pointer items-center justify-center rounded-[25px] border-0 bg-scarlet text-white",
              focusRing,
            )}
          >
            <PhoneIcon size={25} strokeWidth={2.2} className="rotate-[135deg]" />
          </button>
        </div>
      </div>
      <DeviceSheet open={devicesOpen} onClose={() => setDevicesOpen(false)} />
    </div>
  );
}

export function CallScreen({ chatId }: { chatId: string }) {
  const { state: calls, start } = useCalls();
  const [params, setParams] = useSearchParams();
  const media = params.get("media") as CallMedia | null;
  const started = useRef(false);

  // Call buttons link here with ?media=voice or ?media=video. Start once, then drop the parameter
  // so a reload does not ring people again.
  useEffect(() => {
    if (started.current || (media !== "voice" && media !== "video")) return;
    started.current = true;
    const next = new URLSearchParams(params);
    next.delete("media");
    setParams(next, { replace: true });
    if (!calls.active) void start(chatId, media);
  }, [media, chatId, calls.active, params, setParams, start]);

  if (calls.active?.chatId === chatId) return <InCall chatId={chatId} />;
  return <CallLobby chatId={chatId} />;
}
